import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator } from 'react-native';

import { useDispatch, useSelector } from 'react-redux';
import { emptyPainRecord, saveRecord, setRecordToEdit } from '../actions';
import { sync } from '../../Sync/actions';
import RecordSectionDrawer from '../components/RecordSectionDrawer';
import { isEmpty, isSame } from '../constants';
import { getTodayReflection } from '../../../shared/utilities/reflectionutils.js';
import Moment from 'moment';
import { shouldShowDailyReflection } from '../../../shared/utilities/timeutils';
import {
  Container,
  FooterButton,
  Header,
  HeaderIcon,
  Spacer,
  Spinner,
  Text,
  Toast,
} from '../../../design/components';
import strings from '../strings';
import { designConstants } from '../../../design/styles';
import getCurrentScreen from '../../../shared/utilities/getCurrentScreen';
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/core';
import NavigationService from '../../Navigator/navigators/NavigationService';
import {
  trackCompletesRecordCreation,
  trackCompletesRecordEditing,
  trackDuplicatePainRecord,
  trackStartRecordCreation,
} from '../../../shared/utilities/answers';
import Alert from '../../../design/components/Alert';
import Button from '../../../design/components/Button';
import sharedStrings from '../../../shared/strings';
import checkPermissions from '../../Settings/checkPermissions';
import { useBackHandler } from '../../../shared/utilities/useBackHandler';
import useThemeContext from '../../../design/styles/useThemeContext';

export default function CreateRecordScene(props) {
  const dispatch = useDispatch();
  const isTablet = designConstants.useIsTablet();
  const { colors } = useThemeContext();
  const noRecord = useSelector(
    (state) =>
      !state.records.current.record || !state.records.current.record.fields,
  );
  const notificationsViewed = useSelector(
    (state) => state.intro.notificationsViewed,
  );
  const isEmptyRecord = useSelector((state) =>
    isEmpty(state.records.current.record),
  );
  let today = Moment();
  const records = useSelector((state) => state.records.list);
  const record = useSelector((state) => state.records.current.record);
  const createDate = useSelector((state) =>
    state.records.current.record
      ? state.records.current.record.createDate
      : null,
  );
  const recordTime = useSelector((state) =>
    state.records.current.record
      ? state.records.current.record.recordTime
      : null,
  );
  const recordExists = useSelector((state) => !!state.records.current.record);
  const reflectionToday = useSelector((state) =>
    getTodayReflection(state.records.list, today),
  );

  const [navigationCompleted, setNavigationCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigation = useNavigation();
  const route = useRoute();

  let end = today.clone().set({ hour: 12, minute: 0, second: 0 });
  if (today.isBefore(end)) {
    today = today.add(-12, 'hour');
  }

  const paramId = route?.params?.id;

  useEffect(() => {
    if (typeof paramId !== 'undefined') {
      if (paramId > 0 ? record.idOnServer != paramId : record.id != -paramId) {
        const recordToEdit =
          paramId > 0
            ? records.find((rec) => rec.idOnServer == paramId)
            : records.find((rec) => rec.id == -paramId);
        console.log('found record to edit', paramId, recordToEdit);
        if (recordToEdit) {
          dispatch(setRecordToEdit(recordToEdit, true));
        }
      }
    }
    // We need to check only if paramId is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, paramId]);

  const onSavePressed = useCallback(() => {
    if (loading) {
      return;
    }
    setLoading(true);
    let isEdit = createDate;
    let isDuplication = record?.isDuplicate;
    const isFirstRecord = records.length < 1;

    setTimeout(() => {
      dispatch(saveRecord()).then(() => {
        if (isDuplication) {
          trackDuplicatePainRecord();
        } else {
          isEdit
            ? trackCompletesRecordEditing('PainRecord')
            : trackCompletesRecordCreation('PainRecord');
        }
        //Check current time - if its between 8PM and 2AM, show daily reflection screen
        Toast.show({
          supportedOrientations: ['portrait', 'landscape'],
          text: strings.create_record_success_toast_text,
          position: 'bottom',
          duration: 3000,
          buttonText: strings.create_reflection_success_toast_ok_button_text,
        });
        let time = Moment();
        let isBetween = shouldShowDailyReflection(time);

        //Find daily reflection of same date

        dispatch(sync());
        setLoading(false);
        if (
          (notificationsViewed || designConstants.isWeb) &&
          !isEdit &&
          isBetween &&
          !reflectionToday
        ) {
          trackStartRecordCreation('DailyReflection');
          navigation.replace('common', { screen: 'createReflection' });
        } else {
          if (designConstants.isWeb) {
            NavigationService.goBack({ navigation, route });
          } else {
            checkPermissions((result) => {
              if (isFirstRecord && !result && !notificationsViewed) {
                navigation.replace('common', { screen: 'notifications' });
              } else {
                NavigationService.goBack({ navigation, route });
              }
            });
          }
        }
        setTimeout(() => {
          dispatch(emptyPainRecord());
        });
      });
    }, 200);
  }, [
    createDate,
    dispatch,
    loading,
    navigation,
    notificationsViewed,
    record?.isDuplicate,
    records.length,
    reflectionToday,
    route,
  ]);

  const onBackPressed = useCallback(() => {
    if (createDate) {
      const currentRecord = records.find((rec) => rec.createDate == createDate);
      if (isSame(currentRecord, record)) {
        setTimeout(() => {
          dispatch(emptyPainRecord());
        });
        NavigationService.goBack({ navigation, route });
        return;
      }
    }

    let empty = isEmptyRecord;
    if (empty) {
      setTimeout(() => {
        dispatch(emptyPainRecord());
      });
      NavigationService.goBack({ navigation, route });
      return;
    }

    Alert.alert(
      strings.create_record_back_without_save_confirmation_alert_title,
      strings.create_record_back_without_save_confirmation_alert_msg,
      [
        {
          text: strings.create_record_back_without_save_confirmation_cancel_button_text,
          onPress: () => {},
        },
        {
          text: strings.create_record_back_without_save_confirmation_no_button_text,
          onPress: () => {
            // if(this.props.record.createDate)
            setTimeout(() => {
              dispatch(emptyPainRecord());
            });
            NavigationService.goBack({ navigation, route });
          },
        },
        {
          text: strings.create_record_back_without_save_confirmation_yes_button_text,
          style: 'positive',
          onPress: onSavePressed,
        },
      ],
      { cancelable: false, userInterfaceStyle: 'dark' },
    );
  }, [
    createDate,
    dispatch,
    isEmptyRecord,
    navigation,
    onSavePressed,
    record,
    records,
    route,
  ]);

  const backHandler = () => {
    let currentScene = getCurrentScreen();
    if (currentScene !== 'createRecord') {
      return false;
    }
    onBackPressed();
    return true;
  };

  useBackHandler(backHandler);

  // TODO: I don't remember why we needed that initially.
  // But that causes time to be set even after user dismisses record creating
  // useEffect(() => {
  //   if (!recordTime && recordExists) {
  //     dispatch(setCurrentTime(Moment().valueOf(), true));
  //   }
  // }, [dispatch, recordExists, recordTime]);

  useFocusEffect(
    useCallback(() => {
      if (noRecord) {
        dispatch(emptyPainRecord()).then(() => {
          setTimeout(() => {
            setNavigationCompleted(true);
          });
        });
      } else {
        setTimeout(() => {
          setNavigationCompleted(true);
        });
      }
    }, [dispatch, noRecord]),
  );

  return (
    <Container fullHeight>
      <Header menuHidden={designConstants.isWeb}>
        <HeaderIcon back web onPress={onBackPressed} tag={'record_back'} />
        <Text headerTitle>
          {createDate
            ? strings.create_record_edit_record_screen_header
            : strings.create_record_add_record_screen_header}
        </Text>
        <Spacer />
        {isTablet && (
          <>
            <Button
              outlined
              reverse
              text={sharedStrings.go_back_button}
              onPress={onBackPressed}
              tag={'record_back'}
              style={{ marginRight: 8 }}
            />
            {loading ? (
              <Button primary disabled>
                <ActivityIndicator size="small" color={colors.white} />
                <Text
                  style={{
                    textAlign: 'center',
                    marginLeft: 14,
                    color: colors.white,
                    fontSize: 14,
                  }}
                >
                  {strings.create_record_saving}
                </Text>
              </Button>
            ) : (
              <Button
                primary
                onPress={onSavePressed}
                tag={'create_record_apply'}
                text={strings.create_record_apply_button}
              />
            )}
          </>
        )}
      </Header>
      {!navigationCompleted && <Spinner color={colors.buttonBlue} />}
      {navigationCompleted && <RecordSectionDrawer navigation={navigation} />}
      {navigationCompleted && !isTablet && (
        <FooterButton
          onPress={onSavePressed}
          tag={'create_record_apply'}
          loading={loading}
          loadingText={strings.create_record_saving}
          text={strings.create_record_apply_button}
          capital
        />
      )}
    </Container>
  );
}
